import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Brain, RefreshCw, AlertTriangle, Sparkles, PlayCircle } from "lucide-react";

/**
 * 🧠 Conselheiro da Caverna
 *
 * Pulls the Gemini-powered contextual recommendation from
 * `/api/counselor/analysis`. On any failure, silently falls back to the
 * deterministic `next_best_action` passed via props so the Home page is
 * never broken by the AI layer.
 */
function formatRelativePt(iso) {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const diffMin = Math.floor((Date.now() - dt.getTime()) / 60000);
    if (diffMin < 1) return "agora";
    if (diffMin < 60) return `há ${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `há ${d} dias`;
    return dt.toLocaleDateString("pt-BR");
  } catch (_) {
    return null;
  }
}

export default function CounselorCard({ fallbackAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/counselor/analysis");
      setData(res.data);
      setErrorMsg("");
    } catch (_) {
      // Safe fallback — the UI will render the deterministic action.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Local cooldown countdown
  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const t = setInterval(() => {
      setCooldownSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [cooldownSeconds]);

  const handleRefresh = async () => {
    if (refreshing || cooldownSeconds > 0) return;
    setRefreshing(true);
    setErrorMsg("");
    try {
      const res = await api.post("/api/counselor/refresh");
      setData(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail) {
        setCooldownSeconds(Number(detail.cooldown_seconds_remaining || 0));
        setErrorMsg(detail.message || "Aguarde para gerar uma nova análise.");
      } else {
        setErrorMsg("Não foi possível gerar uma nova análise agora.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const headline =
    data?.headline ||
    fallbackAction ||
    "Continue executando o checklist diário da sua operação.";
  const reasoning = data?.reasoning;
  const warnings = data?.warnings || [];
  const source = data?.source;
  const isAI = source === "gemini";
  const recommendedLessonId = data?.recommended_lesson_id;
  const recommendedLessonTitle = data?.recommended_lesson_title;
  const generatedLabel = formatRelativePt(data?.generated_at);

  const cooldownLabel =
    cooldownSeconds > 0
      ? `${Math.floor(cooldownSeconds / 60)}m ${cooldownSeconds % 60}s`
      : null;

  return (
    <div
      data-testid="counselor-card"
      className="rounded-xl border border-[#282D3D] bg-gradient-to-br from-[#1A1030] to-[#14161C] p-4 mb-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
            <Brain className="w-4 h-4 text-purple-300" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest font-extrabold text-purple-300">
                Conselheiro da Caverna
              </span>
              {isAI && (
                <span
                  data-testid="counselor-ai-badge"
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/40 text-[9px] font-extrabold uppercase tracking-widest text-purple-200"
                >
                  <Sparkles className="w-2.5 h-2.5" /> IA
                </span>
              )}
            </div>
            {generatedLabel && (
              <span
                data-testid="counselor-generated-at"
                className="text-[10px] text-gray-500"
              >
                Atualizado {generatedLabel}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || cooldownSeconds > 0 || loading}
          data-testid="counselor-refresh-btn"
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed text-[10px] font-extrabold uppercase tracking-widest text-purple-200"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          {cooldownLabel ? cooldownLabel : refreshing ? "Analisando..." : "Atualizar"}
        </button>
      </div>

      <p
        data-testid="counselor-headline"
        className="text-sm font-semibold text-white leading-snug"
      >
        {loading && !data ? "Analisando sua operação..." : headline}
      </p>

      {reasoning && (
        <p
          data-testid="counselor-reasoning"
          className="mt-1.5 text-[11px] text-gray-400 leading-snug"
        >
          {reasoning}
        </p>
      )}

      {warnings.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="counselor-warnings">
          {warnings.slice(0, 3).map((w, i) => (
            <div
              key={i}
              className="flex items-start gap-1.5 text-[11px] text-amber-200/90 leading-snug"
            >
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5 text-amber-400" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {recommendedLessonId && (
        <Link
          to={`/biblioteca?lesson=${encodeURIComponent(recommendedLessonId)}`}
          data-testid="counselor-lesson-cta"
          className="mt-3 inline-flex items-center gap-2 w-full justify-center px-3 py-2 rounded-lg bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/40 text-purple-100 text-xs font-extrabold uppercase tracking-wider transition-colors"
        >
          <PlayCircle className="w-4 h-4" />
          <span className="truncate">
            Assistir: {recommendedLessonTitle || "Aula recomendada"}
          </span>
        </Link>
      )}

      {errorMsg && cooldownSeconds > 0 && (
        <p
          data-testid="counselor-cooldown-msg"
          className="mt-2 text-[10px] text-amber-300/80"
        >
          {errorMsg}
        </p>
      )}

      {!isAI && !loading && (
        <p className="mt-2 text-[10px] text-gray-500">
          Modo offline — recomendação baseada em regras.
        </p>
      )}
    </div>
  );
}
